import { useEffect, useState } from "react";
import * as THREE from "three";
import { useProjectFocus } from "@/stores/projectFocusStore";
import { FOCUS_DIR } from "./focusUtils";

const BASE_DIR = new THREE.Vector3(...FOCUS_DIR).normalize();
const UP = new THREE.Vector3(0, 1, 0);
const dir = new THREE.Vector3();
const box = new THREE.Box3();
const sphere = new THREE.Sphere();
const focusCenter = new THREE.Vector3();
const delta = new THREE.Vector3();
const lateral = new THREE.Vector3();

// True while another model is focused and this model's bounding sphere sits
// in the corridor between the focus camera and the focused model — i.e. it
// would block the focus view. Components hide themselves while it is true;
// everything restores when the focus changes or exits.
export default function useFocusOcclusionHidden(focusId, groupRef) {
  const focused = useProjectFocus((s) => s.focused);
  const [occluded, setOccluded] = useState(false);

  useEffect(() => {
    const group = groupRef.current;
    if (!focused || focused.id === focusId || !group) {
      setOccluded(false);
      return;
    }
    box.setFromObject(group);
    box.getBoundingSphere(sphere);
    focusCenter.fromArray(focused.center);
    dir.copy(BASE_DIR);
    if (focused.yaw) dir.applyAxisAngle(UP, focused.yaw);
    delta.copy(sphere.center).sub(focusCenter);
    // Depth along the view axis toward the camera; positive = in front of
    // the focused model. Lateral = distance off the camera-to-model axis.
    const depth = delta.dot(dir);
    lateral.copy(delta).addScaledVector(dir, -depth);
    setOccluded(
      depth > 0 && lateral.length() < sphere.radius + (focused.radius ?? 0),
    );
  }, [focused, focusId, groupRef]);

  return occluded;
}
